
import { SimulationConfig, Ball, Vector2D, MorphData, CollisionEvent, BallPiece, EscapeRing, TrailCircle } from '../../types';
import { ShapeUtils } from '../physics/ShapeUtils';
import { AUDIO } from '../../services/AudioService';
import { MS_PER_TICK } from '../../constants';
import { RhythmicPhysics } from './RhythmicPhysics';
import { EscapeService } from '../../services/EscapeService';
import { isInsideCollider } from '../physics/BoundaryCollision';
import { TrailLogic } from './TrailLogic';
import { TrailCirclePool } from '../../services/TrailCirclePool';

export const RhythmicEscapeLogic = {
  update: (
    b: Ball,
    effectiveCfg: SimulationConfig,
    deltaTime: number,
    centerX: number,
    centerY: number,
    escapeRingsRef: React.MutableRefObject<EscapeRing[]>,
    shieldActiveRef: React.MutableRefObject<boolean>,
    shieldBouncesRef: React.MutableRefObject<number>,
    shieldTimeRef: React.MutableRefObject<number>,
    shieldAngleRef: React.MutableRefObject<number>,
    rhythmicTimeScaleRef: React.MutableRefObject<number>,
    rhythmicTimeScaleRateRef: React.MutableRefObject<number>,
    rotRad: number,
    hue: number,
    morphData: MorphData,
    collisionEventsRef: React.MutableRefObject<CollisionEvent[]>,
    persistentSpectrumTargetRef: React.MutableRefObject<Float32Array>,
    trailCirclesRef: React.MutableRefObject<TrailCircle[]>,
    trailCirclePoolRef: React.MutableRefObject<TrailCirclePool>,
    now: number,
    currentTS: number,
    onShake: (intensity: number) => void,
    triggerBlast: (b: {pos: Vector2D, color: string, radius: number}, cmr?: number, gapAngle?: number, morph?: MorphData, animType?: string) => void,
    rhythmicIndexRef: React.MutableRefObject<number>,
    onBounce?: () => void,
    onRingBreak?: (ring: EscapeRing, index: number) => void
  ) => {
    if (b.isBreaking) return;

    const substeps = 4;
    const dt = deltaTime / substeps;
    const g = effectiveCfg.gravity;

    // Movement logic similar to Rhythmic but targeting rings
    const melodySpeed = rhythmicTimeScaleRef.current;
    const visualTS = effectiveCfg.rhythmicBallSpeedDecoupled ? 1.0 : melodySpeed;

    for (let s = 0; s < substeps; s++) {
        // Trail Logic
        if (effectiveCfg.mode === 'TRAIL' || effectiveCfg.trailLength > 0 || effectiveCfg.trailPersistence) {
            TrailLogic.updateTrail(b, effectiveCfg, deltaTime, substeps, currentTS, trailCirclesRef.current, hue, trailCirclePoolRef.current);
        }

        b.vel.y += g * (dt / MS_PER_TICK) * visualTS;

        const oldVel = { ...b.vel };
        const stepX = b.vel.x * (dt / MS_PER_TICK) * visualTS;
        const stepY = b.vel.y * (dt / MS_PER_TICK) * visualTS;
        
        b.pos.x += stepX;
        b.pos.y += stepY;

        const relX = b.pos.x - centerX;
        const relY = b.pos.y - centerY;
        const angle = Math.atan2(relY, relX);
        const dist = Math.sqrt(relX * relX + relY * relY);

        // Determine current target ring
        let targetRing: { radius: number, isShield: boolean, ring?: EscapeRing, index?: number } | null = null;
        
        const shieldRadius = effectiveCfg.rhythmicEscapeShieldRingRadius || 100;
        const innermostEscapeRing = escapeRingsRef.current[0];
        
        // Priority logic: collide with shield if active and ball is inside/at it,
        // otherwise collide with the innermost escape ring.
        // Use <= to ensure shield takes precedence over overlapping rings.
        if (shieldActiveRef.current && (innermostEscapeRing ? shieldRadius <= innermostEscapeRing.radius : true)) {
            targetRing = { radius: shieldRadius, isShield: true };
        } else if (innermostEscapeRing) {
            targetRing = { radius: innermostEscapeRing.radius, isShield: false, ring: innermostEscapeRing, index: effectiveCfg.escapeRingCount - escapeRingsRef.current.length };
        }

        if (!targetRing) {
             // Out of rings, just let it fly or wrap?
             // For now, let it fly.
             continue;
        }

        const lineWidth = effectiveCfg.escapeRingThickness || 4;
        const currentTargetAngle = targetRing.isShield ? shieldAngleRef.current : rotRad;
        const shieldBaseR = effectiveCfg.rhythmicEscapeShieldRingRadius || 100;
        const effectiveShapeR = ShapeUtils.getInterpolatedRadiusAtAngle(angle - currentTargetAngle, morphData.from, morphData.to, morphData.factor, targetRing.radius, shieldBaseR) - lineWidth / 2;

        if (dist + b.radius >= effectiveShapeR && (b.vel.x * relX + b.vel.y * relY > 0)) {
            // Collision potential
            const ballAngle = angle < 0 ? angle + Math.PI * 2 : angle;
            
            let inGap = false;
            if (!targetRing.isShield && targetRing.ring) {
                const ring = targetRing.ring;
                let normAngle = angle;
                if (normAngle < 0) normAngle += Math.PI * 2;
                let ringStart = ring.angle % (Math.PI * 2);
                if (ringStart < 0) ringStart += Math.PI * 2;
                
                let angleDiff = (normAngle - ringStart + Math.PI) % (Math.PI * 2);
                if (angleDiff < 0) angleDiff += Math.PI * 2;
                angleDiff -= Math.PI;
                
                const diff = Math.abs(angleDiff);
                const halfGap = (effectiveCfg.escapeGapSize * Math.PI / 180) / 2;
                const ballAngularWidth = b.radius / ring.radius;
                inGap = diff < (halfGap - ballAngularWidth * 0.8);
            }

            if (inGap) {
                // Pass through!
                // Lock destruction of outer rings if shield is up
                if (shieldActiveRef.current) {
                    continue; 
                }
                if (onRingBreak && targetRing.ring) onRingBreak(targetRing.ring, targetRing.index!);
                escapeRingsRef.current.shift();
                continue; // Don't bounce, continue update to see if we hit next ring
            } else {
                // BOUNCE
                const speed = Math.sqrt(b.vel.x*b.vel.x + b.vel.y*b.vel.y) * visualTS;
                const timeSinceCollisionMs = ((dist + b.radius - effectiveShapeR) / Math.max(speed, 0.001)) * MS_PER_TICK;
                const exactCollisionTime = AUDIO.initCtx().currentTime - (timeSinceCollisionMs / 1000) + (effectiveCfg.rhythmicSyncOffset / 1000);

                const localNormal = ShapeUtils.getNormal({ x: relX * Math.cos(-currentTargetAngle) - relY * Math.sin(-currentTargetAngle), y: relX * Math.sin(-currentTargetAngle) + relY * Math.cos(-currentTargetAngle) }, morphData.from, targetRing.radius - lineWidth / 2, morphData.to, morphData.factor, shieldBaseR);
                const worldNormal = { x: localNormal.x * Math.cos(currentTargetAngle) - localNormal.y * Math.sin(currentTargetAngle), y: localNormal.x * Math.sin(currentTargetAngle) + localNormal.y * Math.cos(currentTargetAngle) };
                
                // Push back to surface
                b.pos.x -= worldNormal.x * (dist + b.radius - effectiveShapeR + 1.0);
                b.pos.y -= worldNormal.y * (dist + b.radius - effectiveShapeR + 1.0);

                // Shield depletion
                if (shieldActiveRef.current) {
                    if (effectiveCfg.rhythmicEscapeShieldRingDestroyMode === 'BOUNCES') {
                        shieldBouncesRef.current--;
                        if (shieldBouncesRef.current <= 0) {
                            shieldActiveRef.current = false;
                            triggerBlast({ pos: { x: centerX, y: centerY }, color: '#ffffff', radius: targetRing.radius }, targetRing.radius, 0, morphData, 'SHOCKWAVE');
                        }
                    }
                }

                // Audio Logic
                const step = AUDIO.getNextRhythmicStep(effectiveCfg.selectedMelody, effectiveCfg, rhythmicIndexRef.current);
                AUDIO.playRhythmicStep(step.notesToPlay, effectiveCfg, exactCollisionTime, melodySpeed);
                rhythmicIndexRef.current = step.nextIndex;
                
                if (onBounce) onBounce();
                
                collisionEventsRef.current.push({ 
                    id: Math.random().toString(), 
                    pos: { x: b.pos.x + worldNormal.x * b.radius, y: b.pos.y + worldNormal.y * b.radius }, 
                    startTime: now - timeSinceCollisionMs, 
                    radius: b.radius * 2.5, 
                    color: b.color, 
                    effectType: effectiveCfg.collisionEffect, 
                    duration: effectiveCfg.collisionEffectDuration || 800 
                });

                onShake(5 * effectiveCfg.shakeIntensityMultiplier);

                if (effectiveCfg.rhythmicEscapeAutoSync) {
                    // Next bounce prediction
                    const remainingTimeSec = Math.max(0.005, (Math.max(10, step.totalDuration) / Math.max(0.01, melodySpeed)) / 1000 - (timeSinceCollisionMs / 1000));
                    const T = Math.max(0.1, remainingTimeSec / (MS_PER_TICK / 1000));
                    
                    const nextTargetR = shieldActiveRef.current ? effectiveCfg.rhythmicEscapeShieldRingRadius : (escapeRingsRef.current[0]?.radius || targetRing.radius);
                    RhythmicPhysics.calculateNextBounce(b, effectiveCfg, centerX, centerY, nextTargetR, b.radius, morphData, rotRad, visualTS, melodySpeed, Math.max(10, step.totalDuration), T, timeSinceCollisionMs, now, g, substeps, oldVel, worldNormal);
                } else {
                    // Standard Reflection
                    const dot = b.vel.x * worldNormal.x + b.vel.y * worldNormal.y;
                    b.vel.x -= 2 * dot * worldNormal.x;
                    b.vel.y -= 2 * dot * worldNormal.y;
                    b.vel.x *= (effectiveCfg.restitution || 1);
                    b.vel.y *= (effectiveCfg.restitution || 1);
                }
                
                break; // Break substeps after bounce
            }
        }
    }

    // Shield Time depletion
    if (shieldActiveRef.current && effectiveCfg.rhythmicEscapeShieldRingDestroyMode === 'TIME') {
        shieldTimeRef.current -= deltaTime / 1000;
        if (shieldTimeRef.current <= 0) {
            shieldActiveRef.current = false;
            triggerBlast({ pos: { x: centerX, y: centerY }, color: '#ffffff', radius: effectiveCfg.rhythmicEscapeShieldRingRadius }, effectiveCfg.rhythmicEscapeShieldRingRadius, 0, morphData, 'SHOCKWAVE');
        }
    }
  },

  init: (
    effectiveCfg: SimulationConfig,
    escapeRingsRef: React.MutableRefObject<EscapeRing[]>,
    initialEscapeRingCountRef: React.MutableRefObject<number>,
    shieldActiveRef: React.MutableRefObject<boolean>,
    shieldBouncesRef: React.MutableRefObject<number>,
    shieldTimeRef: React.MutableRefObject<number>,
    shieldAngleRef: React.MutableRefObject<number>,
    rhythmicIndexRef: React.MutableRefObject<number>,
    createBall: (overrides: Partial<Ball>) => Ball,
    centerX: number,
    centerY: number
  ): Ball[] => {
    rhythmicIndexRef.current = 0;
    escapeRingsRef.current = EscapeService.generateRings(effectiveCfg);
    initialEscapeRingCountRef.current = escapeRingsRef.current.length;
    
    shieldActiveRef.current = effectiveCfg.rhythmicEscapeShieldRing;
    shieldBouncesRef.current = effectiveCfg.rhythmicEscapeShieldRingDestroyValue;
    shieldTimeRef.current = effectiveCfg.rhythmicEscapeShieldRingDestroyValue;
    shieldAngleRef.current = 0;

    const total = 1; // Start with one for sync
    const now = Date.now();
    
    return [
        createBall({ 
            id: `rhythmic-escape-ball`, 
            pos: { x: centerX, y: centerY }, 
            vel: { x: 0, y: 15 }, // Give it a little start
            radius: effectiveCfg.ballRadius,
            color: effectiveCfg.ball1Color,
            spawnTime: now
        })
    ];
  }
};
